import Link from "next/link";
import CTAButton from "./CTAButton";
import Image from "next/image";

interface PricingCardProps {
    title: string;
    price: string;
    description: string;
    imageSrc: string;
    icon?: string | React.ReactNode;
    ctaLabel?: string;
    ctaHref?: string;
    recommended?: boolean;
    soldOut?: boolean;
    isClosed?: boolean;
    isTemporarilyClosed?: boolean;
    note?: string;
}

export default function PricingCard({
    title,
    price,
    description,
    imageSrc,
    icon,
    ctaLabel = "Inscripción",
    ctaHref = "/inscripcion",
    recommended = false,
    soldOut = false,
    isClosed = false,
    isTemporarilyClosed = false,
    note,
}: PricingCardProps) {
    const isActuallyClosed = soldOut || isClosed || isTemporarilyClosed;
    const displayCtaLabel = isClosed 
        ? "Inscripciones Cerradas" 
        : (soldOut 
            ? "Plazas Agotadas" 
            : (isTemporarilyClosed 
                ? "REAPERTURA EN BREVE" 
                : ctaLabel));
    const ctaVariant = isTemporarilyClosed 
        ? "disabled" 
        : (isActuallyClosed 
            ? "danger" 
            : (recommended ? "primary" : "outline"));

    return (
        <div
            className={`bg-surface-light dark:bg-surface-dark p-8 border transition-colors group rounded-lg relative ${recommended ? 'border-2 border-primary shadow-2xl transform md:-translate-y-4' : 'border-gray-200 dark:border-gray-800'
                } ${soldOut ? '' : 'hover:border-primary'}`}
        >
            {recommended && (
                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-primary text-black text-xs font-bold uppercase px-3 py-1 rounded-sm shadow-md">
                    Recomendado
                </div>
            )}

            {/* Icon Circle */}
            <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6 transition-colors ${recommended
                ? 'bg-secondary text-white'
                : 'bg-gray-100 dark:bg-zinc-800 text-gray-500 group-hover:bg-primary group-hover:text-black'
                }`}>
                {/* Material Icon or Custom Content */}
                {typeof icon === 'string' ? (
                    <span className="material-icons text-3xl">{icon}</span>
                ) : (
                    icon || <span className="material-icons text-3xl">euro</span>
                )}
            </div>

            <h3 className={`font-accent font-bold uppercase text-lg text-center mb-1 ${recommended ? 'text-secondary dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'
                }`}>
                {title}
            </h3>

            <p className="text-xs text-gray-400 text-center mb-6">{description}</p>

            <div className="text-5xl font-display text-center text-black dark:text-white mb-6">
                {price}
            </div>

            <div className="text-center">
                {isActuallyClosed && !isTemporarilyClosed && (
                    <div className="text-[#FF3131] font-bold uppercase text-sm mb-4">
                        {isClosed ? "LO SENTIMOS" : "PLAZAS AGOTADAS"}
                    </div>
                )}
                <CTAButton
                    label={displayCtaLabel}
                    href={isActuallyClosed ? "#" : (ctaHref ?? "#")}
                    variant={ctaVariant}
                    className="w-full text-xs py-3"
                    external={ctaHref?.startsWith("http")}
                />
                {note && (
                    <p className="mt-4 text-xs text-zinc-500 dark:text-zinc-400 italic leading-relaxed text-center font-light">
                        {note}
                    </p>
                )}
            </div>
        </div>
    );
}
